/**
 * W2.4 — Feld-Tabelle des Loaders (Spiegel von apps/api/src/lib/widget-formular.ts).
 *
 * WARUM EINE ZWEITE TABELLE (Entscheidung E-W12, docs/LOOP-WIDGETS.md § 2):
 * Der Loader kann `lib/widget-formular.ts` nicht importieren — die Datei zieht `zod` mit sich,
 * und dieses Bundle laeuft auf FREMDEN Websites unter einer harten 30-kB-Ratsche (KONZEPT § 5.3).
 * Ein gemeinsames zod-freies Paket waere die dritte Moeglichkeit; es kostet ein neues
 * Workspace-Paket samt Build-/tsconfig-Verdrahtung fuer ~40 Zeilen Daten, die zwei sehr
 * unterschiedliche Ziele bedienen (Worker-Validierung vs. ES2019-Browser-Rendering). Gewaehlt ist
 * deshalb der kleinste tragfaehige Weg — eigene Tabelle + **Drift-Ratsche**: ein Test in
 * apps/api/test/widget-formular.test.ts liest DIESE Datei und vergleicht Schluessel, Typ,
 * Beschriftung und Pflicht-Flag jedes Feldes gegen das Modell. Genau dasselbe Muster haelt seit
 * W2.1 die Anzeige-Tabelle in apps/web/src/pages/widgets-tab.tsx deckungsgleich.
 *
 * Deshalb gilt hier: **Reihenfolge und Eigenschaften-Reihenfolge (key, typ, label, pflicht)
 * nicht umsortieren** — die Ratsche liest sie in dieser Form.
 *
 * Was hier BEWUSST fehlt: jede Validierung. Das Formular im Browser ist Darstellung, nie
 * Wahrheit — geprueft wird ausschliesslich serverseitig gegen `published_config` (W2.2,
 * routes/widgets-public.ts). Diese Tabelle liefert nur Beschriftung, Eingabetyp und die
 * Bequemlichkeits-Validierung des Browsers (`required`, `maxlength`, `type=email`).
 */

export type FeldTyp = "text" | "textarea" | "select" | "checkbox" | "date" | "email" | "tel" | "number" | "time" | "file";

/** Ein Feld, wie der Loader es rendert. Untermenge des Modell-`Feld` (nur Darstellungs-Belange). */
export interface Feld {
  key: string;
  typ: FeldTyp;
  label: string;
  pflicht: boolean;
  maxLength?: number;
  /** Sichtbare Auswahltexte (select). */
  optionen?: readonly string[];
  /** Gespeicherte Werte hinter den Auswahltexten (nur feste Auswahlfelder, heute `serviceKind`). */
  optionenWerte?: readonly string[];
  /** „Mindestens eines dieser Felder“ — heute nur E-Mail ODER Telefon. */
  gruppe?: "kontakt";
  /** Vom Loader ohne Zutun des Gastes erfasst: nicht sichtbar, nicht ausfuellbar. */
  automatisch?: boolean;
  /** Browser-Autofill (nur wo es eindeutig ist). */
  autocomplete?: string;
}

/** Schicht 1 — fix, in keiner Konfiguration, nie abwaehlbar. */
export const PFLICHTFELDER: readonly Feld[] = [
  { key: "customerName", typ: "text", label: "Name", pflicht: true, maxLength: 200, autocomplete: "name" },
  { key: "customerEmail", typ: "email", label: "E-Mail", pflicht: false, maxLength: 320, gruppe: "kontakt", autocomplete: "email" },
  { key: "phone", typ: "tel", label: "Telefon", pflicht: false, maxLength: 60, gruppe: "kontakt", autocomplete: "tel" },
  { key: "eventDate", typ: "date", label: "Wunschtermin", pflicht: true },
  { key: "guests", typ: "number", label: "Personenzahl", pflicht: true },
  // Buchungsstrecke D3: freiwillig — der Anlass kommt aus den Kacheln (kern.ts ANLAESSE).
  { key: "message", typ: "textarea", label: "Ihre Nachricht", pflicht: false, maxLength: 5000 },
];

/** Schicht 2a — vom Betreiber an-/abwaehlbar (`formular.standardfelder`). */
export const STANDARDFELDER: readonly Feld[] = [
  { key: "company", typ: "text", label: "Firma", pflicht: false, maxLength: 200, autocomplete: "organization" },
  // Antoine, 21.08.2026: "Im Haus"/"Vor Ort" verwirrte Gaeste (unklar, wessen "Haus"/"Ort"
  // gemeint ist). Neu eindeutig UND mandantenneutral formuliert (dieser Katalog gilt fuer alle
  // Betriebe auf der Plattform, nicht nur ein einzelnes Restaurant) - die technischen Werte
  // ["event","catering"] bleiben unveraendert, nur optionen/label sind Text.
  { key: "serviceKind", typ: "select", label: "Wo findet die Veranstaltung statt?", pflicht: false, optionen: ["In unseren Räumen", "Als Catering-Lieferung zu Ihnen"], optionenWerte: ["event", "catering"] },
  { key: "deliveryAddress", typ: "textarea", label: "Lieferadresse", pflicht: false, maxLength: 500 },
  { key: "budget", typ: "text", label: "Budgetvorstellung (Freitext)", pflicht: false, maxLength: 100 },
  { key: "dietaryRequirements", typ: "textarea", label: "Unverträglichkeiten / Ernährung", pflicht: false, maxLength: 1000 },
  { key: "dateFlexible", typ: "checkbox", label: "Termin ist flexibel", pflicht: false },
  { key: "arrivalTime", typ: "text", label: "Gewünschte Uhrzeit", pflicht: false, maxLength: 20 },
  { key: "preferredMenu", typ: "textarea", label: "Menü-Wunsch", pflicht: false, maxLength: 500 },
  { key: "format", typ: "text", label: "Format der Veranstaltung", pflicht: false, maxLength: 120 },
];

/**
 * Schicht 2b — automatisch erfasst. Der Loader schickt sie mit; sie werden NIE gerendert.
 * Die Laengen sind hier nicht Zierde: der Server weist eine zu lange Angabe mit 422 ab
 * (`pruefeWert`), und eine 300 Zeichen lange Kampagnen-URL duerfte niemals eine echte Anfrage
 * kosten — der Loader kuerzt deshalb auf genau diese Grenzen (kuerzeWert in kern.ts).
 */
export const AUTOMATISCHE_FELDER: readonly Feld[] = [
  { key: "originalPage", typ: "text", label: "Seite", pflicht: false, maxLength: 500, automatisch: true },
  { key: "referrer", typ: "text", label: "Verweisende Seite", pflicht: false, maxLength: 500, automatisch: true },
  { key: "utmSource", typ: "text", label: "utm_source", pflicht: false, maxLength: 200, automatisch: true },
  { key: "utmMedium", typ: "text", label: "utm_medium", pflicht: false, maxLength: 200, automatisch: true },
  { key: "utmCampaign", typ: "text", label: "utm_campaign", pflicht: false, maxLength: 200, automatisch: true },
  { key: "utmTerm", typ: "text", label: "utm_term", pflicht: false, maxLength: 200, automatisch: true },
  { key: "utmContent", typ: "text", label: "utm_content", pflicht: false, maxLength: 200, automatisch: true },
];

/** Die freien Felder, wie sie in der publizierten Konfiguration stehen. */
export interface FreiesFeld {
  key: string;
  typ: FreierFeldTyp;
  label: string;
  pflicht: boolean;
  maxLength?: number;
  optionen?: string[];
}

export interface FormularKonfig {
  standardfelder: string[];
  freieFelder: FreiesFeld[];
}

const STANDARD_NACH_KEY = new Map(STANDARDFELDER.map((f) => [f.key, f]));

/**
 * Die Typen, die ein freies Feld tragen darf — WORTGLEICH `FREIE_FELDTYPEN` in
 * apps/api/src/lib/widget-formular.ts. Eine Ratsche in apps/api/test/widget-formular.test.ts
 * vergleicht diese Liste mit dem Modell UND mit der Auswahl im Editor; sie steht hier
 * exportiert, damit die Ratsche sie als Text findet (E-W12: kein gemeinsames Paket, weil das
 * Bundle unter einer 30-kB-Ratsche laeuft und `zod` nicht mitkommen darf).
 */
export const FREIE_TYPEN = ["text", "textarea", "select", "checkbox", "date", "email", "tel", "time", "file"] as const;
export type FreierFeldTyp = (typeof FREIE_TYPEN)[number];

function istFreierTyp(wert: unknown): wert is FreierFeldTyp {
  return typeof wert === "string" && (FREIE_TYPEN as readonly string[]).indexOf(wert) >= 0;
}

/**
 * `config.formular` defensiv lesen. Alles Unerwartete wird ignoriert statt zu werfen — der Loader
 * laeuft auf einer fremden Website und darf dort nie sichtbar scheitern (§ 8). Ein Feld ohne
 * brauchbaren Schluessel/Typ/Beschriftung wird ausgelassen: lieber ein Formular ohne dieses eine
 * Feld als gar keins.
 */
export function leseFormularKonfig(config: Record<string, unknown> | null | undefined): FormularKonfig | null {
  const roh = config ? config.formular : null;
  if (typeof roh !== "object" || roh === null || Array.isArray(roh)) return null;
  const q = roh as { standardfelder?: unknown; freieFelder?: unknown };

  const standardfelder: string[] = [];
  if (Array.isArray(q.standardfelder)) {
    for (const k of q.standardfelder) {
      if (typeof k === "string" && STANDARD_NACH_KEY.has(k) && standardfelder.indexOf(k) < 0) standardfelder.push(k);
    }
  }

  const freieFelder: FreiesFeld[] = [];
  if (Array.isArray(q.freieFelder)) {
    for (const e of q.freieFelder) {
      if (typeof e !== "object" || e === null || Array.isArray(e)) continue;
      const f = e as Record<string, unknown>;
      if (typeof f.key !== "string" || f.key.length === 0) continue;
      if (!istFreierTyp(f.typ)) continue;
      if (typeof f.label !== "string" || f.label.trim().length === 0) continue;
      const optionen = Array.isArray(f.optionen) ? f.optionen.filter((o): o is string => typeof o === "string") : undefined;
      if (f.typ === "select" && (!optionen || optionen.length === 0)) continue;
      freieFelder.push({
        key: f.key,
        typ: f.typ,
        label: f.label.trim(),
        pflicht: f.pflicht === true,
        ...(typeof f.maxLength === "number" && f.maxLength > 0 ? { maxLength: f.maxLength } : {}),
        ...(optionen ? { optionen } : {}),
      });
    }
  }
  return { standardfelder, freieFelder };
}

/**
 * Die SICHTBAREN Felder in Renderreihenfolge: Pflichtfelder, gewaehlte Standardfelder, freie
 * Felder. Die automatisch erfassten sind bewusst NICHT dabei (sie werden nicht gerendert) —
 * das Gegenstueck zu `effektiveFelder` im Modell, das sie fuer die Pruefung mitfuehrt.
 */
export function sichtbareFelder(konfig: FormularKonfig | null): Feld[] {
  const standard = konfig
    ? konfig.standardfelder.map((k) => STANDARD_NACH_KEY.get(k)).filter((f): f is Feld => f != null)
    : [];
  const frei: Feld[] = konfig
    ? konfig.freieFelder.map((f) => ({
        key: f.key,
        typ: f.typ,
        label: f.label,
        pflicht: f.pflicht,
        ...(f.maxLength !== undefined ? { maxLength: f.maxLength } : {}),
        ...(f.optionen ? { optionen: f.optionen } : {}),
      }))
    : [];
  return [...PFLICHTFELDER, ...standard, ...frei];
}
